import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, /** @type {EventListener} */ (this.#handleCartUpdate));
    document.addEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (this.handleDiscountUpdate));
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#debouncedOnChange))
    );
    document.addEventListener('change', this.#onSellingPlanSelectorChange);

    this.#syncAllSellingPlanLabels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, /** @type {EventListener} */ (this.#handleCartUpdate));
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#debouncedOnChange))
    );
    document.removeEventListener('change', this.#onSellingPlanSelectorChange);
  }

  /**
   * Handles cart subscription frequency selector changes.
   * Prefers in-place line updates to preserve row ordering and falls back to remove-and-add.
   * @param {Event} event
   */
  #onSellingPlanSelectorChange = async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.hasAttribute('data-cart-selling-plan-selector')) return;
    if (!this.contains(target)) return;

    const newSellingPlanId = target.value;
    const currentSellingPlanId = target.dataset.currentSellingPlanId || '';
    if (newSellingPlanId === currentSellingPlanId) return;

    this.#updateSellingPlanLabel(target);

    const selectedOption = target.options[target.selectedIndex];
    const selectedVariantId = Number(selectedOption?.dataset.variantId || target.dataset.variantId || 0);

    const line = Number(target.dataset.cartLine || 0);
    const quantity = Number(target.dataset.quantity || 1);
    if (!line || !selectedVariantId || quantity < 1) return;
    const lineItemRow = this.refs.cartItemRows[line - 1];

    const oldValue = currentSellingPlanId;
    target.disabled = true;
    target.setAttribute('aria-busy', 'true');
    lineItemRow?.classList.add('cart-items__table-row--updating');
    this.#disableCartItems();

    try {
      const properties = this.#parseLineItemProperties(target.dataset.itemProperties);
      if (newSellingPlanId) {
        await this.#switchLineToSellingPlan({
          line,
          variantId: selectedVariantId,
          quantity,
          sellingPlanId: Number(newSellingPlanId),
          properties,
        });
      } else {
        await this.#switchLineToOneTime({
          line,
          variantId: selectedVariantId,
          quantity,
          properties,
        });
      }
    } catch (error) {
      console.error(error);
      target.value = oldValue;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      target.disabled = false;
      target.removeAttribute('aria-busy');
      lineItemRow?.classList.remove('cart-items__table-row--updating');
      this.#enableCartItems();
    }
  };

  /**
   * Handles one-time line "Subscribe & Save" button clicks.
   * @param {Event} event
   */
  #onSubscribeTriggerClick = async (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-cart-subscribe-trigger]') : null;
    if (!(trigger instanceof HTMLButtonElement)) return;
    if (!this.contains(trigger)) return;

    const line = Number(trigger.dataset.cartLine || 0);
    const variantId = Number(trigger.dataset.variantId || 0);
    const quantity = Number(trigger.dataset.quantity || 1);
    const sellingPlanId = Number(trigger.dataset.defaultSellingPlanId || 0);
    if (!line || !variantId || quantity < 1 || !sellingPlanId) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const properties = this.#parseLineItemProperties(trigger.dataset.itemProperties);

    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    lineItemRow?.classList.add('cart-items__table-row--updating');
    this.#disableCartItems();

    try {
      await this.#switchLineToSellingPlan({
        line,
        variantId,
        quantity,
        sellingPlanId,
        properties,
      });
    } catch (error) {
      console.error(error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      trigger.disabled = false;
      trigger.removeAttribute('aria-busy');
      lineItemRow?.classList.remove('cart-items__table-row--updating');
      this.#enableCartItems();
    }
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (!this.#morphCartItemsComponent(parsedResponseText.sections[this.sectionId])) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      if (!this.#morphCartItemsComponent(cartItemsHtml)) {
        morphSection(this.sectionId, cartItemsHtml);

        // Update button states for all cart quantity selectors after morph
        this.#updateCartQuantitySelectorButtonStates();
      }
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Morph only this cart-items-component to keep the cart drawer dialog mounted/open.
   * @param {string | undefined} sectionHtml
   * @returns {boolean}
   */
  #morphCartItemsComponent(sectionHtml) {
    if (!sectionHtml) return false;

    const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const updatedCartItemsComponent = parsedSection.querySelector('cart-items-component');

    if (!updatedCartItemsComponent) return false;

    morph(this, updatedCartItemsComponent);
    this.#updateCartQuantitySelectorButtonStates();
    return true;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #updateSellingPlanLabel(select) {
    const label = this.querySelector(`label[data-cart-selling-plan-label][for="${select.id}"]`);
    if (!(label instanceof HTMLLabelElement)) return;

    const selectedOption = select.options[select.selectedIndex];
    label.textContent = this.#formatSellingPlanLabel((selectedOption?.textContent || 'Subscribe & Save').trim());
  }

  #syncAllSellingPlanLabels() {
    const selectors = this.querySelectorAll('select[data-cart-selling-plan-selector]');
    for (const select of selectors) {
      if (select instanceof HTMLSelectElement) {
        this.#updateSellingPlanLabel(select);
      }
    }
  }

  /**
   * @param {string} text
   * @returns {number}
   */
  #extractPackCountFromPlanText(text) {
    const normalized = (text || '')
      .toLowerCase()
      .replace(/[-/,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const explicitMonthsMatch = normalized.match(/(\d+)\s*month/);
    if (explicitMonthsMatch) {
      const parsed = Number(explicitMonthsMatch[1]);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    if (normalized.includes('monthly') || normalized.includes('every month')) {
      return 1;
    }

    return 1;
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  #extractFrequencyFromPlanText(text) {
    const normalized = (text || '')
      .toLowerCase()
      .replace(/[-/,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const everyMatch = normalized.match(/every\s+(\d+)\s*(day|week|month|year)s?/);
    if (everyMatch) {
      const count = Number(everyMatch[1]);
      const unit = everyMatch[2];
      return `Every ${count} ${unit}${count > 1 ? 's' : ''}`;
    }

    if (normalized.includes('daily') || normalized.includes('every day')) return 'Daily';
    if (normalized.includes('weekly') || normalized.includes('every week')) return 'Weekly';
    if (normalized.includes('biweekly')) return 'Every 2 weeks';
    if (normalized.includes('monthly') || normalized.includes('every month')) return 'Monthly';
    if (normalized.includes('quarterly') || normalized.includes('every 3 month')) return 'Quarterly';
    if (normalized.includes('yearly') || normalized.includes('annually') || normalized.includes('every year')) {
      return 'Yearly';
    }

    return text || 'Subscription';
  }

  /**
   * @param {string} planText
   * @returns {string}
   */
  #formatSellingPlanLabel(planText) {
    const packCount = this.#extractPackCountFromPlanText(planText);
    return `${packCount} pack`;
  }

  /**
   * @param {any} cartResponse
   */
  #applyCartResponse(cartResponse) {
    const newSectionHTML = cartResponse?.sections?.[this.sectionId];
    if (!newSectionHTML) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    this.#updateQuantitySelectors(cartResponse);

    const parsedSection = new DOMParser().parseFromString(newSectionHTML, 'text/html');
    const newCartHiddenItemCount = parsedSection.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent(cartResponse, this.sectionId, {
        source: 'cart-items-component',
        itemCount: newCartItemCount,
        sections: cartResponse.sections,
      })
    );

    if (!this.#morphCartItemsComponent(newSectionHTML)) {
      morphSection(this.sectionId, newSectionHTML);
      this.#updateCartQuantitySelectorButtonStates();
    }
  }

  /**
   * @returns {string[]}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate);
  }

  /**
   * @param {string | undefined} propertiesJson
   * @returns {Record<string, string>}
   */
  #parseLineItemProperties(propertiesJson) {
    if (!propertiesJson) return {};

    try {
      const parsed = JSON.parse(propertiesJson);
      if (!parsed || typeof parsed !== 'object') return {};

      return Object.entries(parsed).reduce((acc, [key, value]) => {
        if (value == null || value === '') return acc;
        acc[key] = String(value);
        return acc;
      }, /** @type {Record<string, string>} */ ({}));
    } catch {
      return {};
    }
  }

  /**
   * @param {{
   *  line: number,
   *  variantId: number,
   *  quantity: number,
   *  sellingPlanId: number,
   *  properties: Record<string, string>
   * }} config
   */
  async #switchLineToSellingPlan(config) {
    const sectionsToUpdate = this.#getSectionsToUpdate();

    let cartResponse = await this.#updateLineSellingPlan({
      line: config.line,
      quantity: config.quantity,
      sellingPlanId: config.sellingPlanId,
      sectionsToUpdate,
    });

    if (!cartResponse) {
      await this.#clearLineItem(config.line, sectionsToUpdate);
      cartResponse = await this.#addLineItemWithSellingPlan({
        variantId: config.variantId,
        quantity: config.quantity,
        sellingPlanId: config.sellingPlanId,
        properties: config.properties,
        sectionsToUpdate,
      });
    }

    this.#applyCartResponse(cartResponse);
  }

  /**
   * @param {{
   *  line: number,
   *  variantId: number,
   *  quantity: number,
   *  properties: Record<string, string>
   * }} config
   */
  async #switchLineToOneTime(config) {
    const sectionsToUpdate = this.#getSectionsToUpdate();

    let cartResponse = await this.#updateLineToOneTime({
      line: config.line,
      quantity: config.quantity,
      sectionsToUpdate,
    });

    if (!cartResponse) {
      await this.#clearLineItem(config.line, sectionsToUpdate);
      cartResponse = await this.#addLineItemOneTime({
        variantId: config.variantId,
        quantity: config.quantity,
        properties: config.properties,
        sectionsToUpdate,
      });
    }

    this.#applyCartResponse(cartResponse);
  }

  /**
   * @param {number} line
   * @param {string[]} sectionsToUpdate
   */
  async #clearLineItem(line, sectionsToUpdate) {
    const clearBody = JSON.stringify({
      line,
      quantity: 0,
      sections: sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    const clearResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: clearBody }));
    const clearData = await clearResponse.json();

    if (clearData?.errors) {
      throw new Error(typeof clearData.errors === 'string' ? clearData.errors : 'Unable to remove original cart line');
    }
  }

  /**
   * Attempts to update selling plan in place for a cart line.
   * Returns null when the endpoint ignores/rejects selling_plan so caller can fallback.
   * @param {{ line: number, quantity: number, sellingPlanId: number, sectionsToUpdate: string[] }} config
   * @returns {Promise<any | null>}
   */
  async #updateLineSellingPlan(config) {
    const body = JSON.stringify({
      line: config.line,
      quantity: config.quantity,
      selling_plan: config.sellingPlanId,
      sections: config.sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
    const data = await response.json();

    if (data?.errors) return null;

    const lineItem = Array.isArray(data?.items) ? data.items[config.line - 1] : null;
    const appliedPlanId = Number(lineItem?.selling_plan_allocation?.selling_plan?.id || 0);

    if (appliedPlanId !== config.sellingPlanId) return null;

    return data;
  }

  /**
   * Attempts to clear selling plan in place for a cart line.
   * Returns null when the endpoint ignores/rejects selling_plan change so caller can fallback.
   * @param {{ line: number, quantity: number, sectionsToUpdate: string[] }} config
   * @returns {Promise<any | null>}
   */
  async #updateLineToOneTime(config) {
    const body = JSON.stringify({
      line: config.line,
      quantity: config.quantity,
      selling_plan: null,
      sections: config.sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
    const data = await response.json();

    if (data?.errors) return null;

    const lineItem = Array.isArray(data?.items) ? data.items[config.line - 1] : null;
    const appliedPlanId = Number(lineItem?.selling_plan_allocation?.selling_plan?.id || 0);

    if (appliedPlanId !== 0) return null;

    return data;
  }

  /**
   * @param {{
   *  variantId: number,
   *  quantity: number,
   *  sellingPlanId: number,
   *  properties: Record<string, string>,
   *  sectionsToUpdate: string[]
   * }} config
   */
  async #addLineItemWithSellingPlan(config) {
    const addBody = JSON.stringify({
      items: [
        {
          id: config.variantId,
          quantity: config.quantity,
          selling_plan: config.sellingPlanId,
          properties: config.properties,
        },
      ],
      sections: config.sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    const addResponse = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: addBody }));
    const addData = await addResponse.json();

    if (addData?.status || addData?.errors) {
      throw new Error(addData?.description || addData?.message || addData?.errors || 'Unable to add updated subscription line');
    }

    return addData;
  }

  /**
   * @param {{
   *  variantId: number,
   *  quantity: number,
   *  properties: Record<string, string>,
   *  sectionsToUpdate: string[]
   * }} config
   */
  async #addLineItemOneTime(config) {
    const addBody = JSON.stringify({
      items: [
        {
          id: config.variantId,
          quantity: config.quantity,
          properties: config.properties,
        },
      ],
      sections: config.sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    const addResponse = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: addBody }));
    const addData = await addResponse.json();

    if (addData?.status || addData?.errors) {
      throw new Error(addData?.description || addData?.message || addData?.errors || 'Unable to add updated one-time line');
    }

    return addData;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
